import React, { useMemo, useState } from "react";
import moment from "moment";
import "moment/locale/gu";
import { useNavigate } from "react-router-dom";
import useConfirmPopup from "../../context/useConfirmPopup";
import { PaymentsProps } from "../../types/payments";
import { useAppDispatch } from "../../app/hooks";
import {
  setEditPayment,
  toggleCreateModal,
} from "../../features/payments/paymentsSlice";
import { GET } from "../../services/axios.service";
import { toast } from "react-toastify";
import { toastStyles } from "../../static/styles";
import { useCanAccess } from "../../hooks/useCanAccess";
import { t } from "../../locales";
import { route } from "../../static/route";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import {
  FileText,
  Pencil,
  Trash2,
  Loader2,
  ShoppingCart,
  ExternalLink,
  Wallet,
  StickyNote,
  ArrowLeftRight,
} from "lucide-react";

type Props = {
  payments: PaymentsProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  handleDeletePayment: (id: number, password: string) => void;
};

const formatINR = (val: number | null | undefined) =>
  (Number(val) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Source-linked rows are owned by another module — hide edit/delete here.
const isSourceLinked = (p: PaymentsProps): boolean =>
  Boolean(p.source_type) || Boolean(p.process_receive_id);

const PaymentList = ({
  payments,
  pageNumber,
  pageSize,
  isLoading,
  handleDeletePayment,
}: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirmDelete = useConfirmPopup();
  const canAccess = useCanAccess();
  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  moment.locale("gu");

  // Classify a payment for display purposes.
  const classifyRow = (payment: PaymentsProps) => {
    const isInvoice = payment.status === 0;
    const isReceived = payment.status === 1;
    const isAdjustment = payment.status === 2;
    let credit = 0;
    let debit = 0;
    const amount = Number(payment.amount) || 0;
    if (isInvoice) credit = amount;
    else if (isReceived) debit = amount;
    else if (isAdjustment) {
      if (payment.type === 0) credit = amount;
      else debit = amount;
    }
    return { isInvoice, isReceived, isAdjustment, credit, debit };
  };

  // Running balance per row. Uses backend-provided `running_balance` or
  // `previous_due` when available; otherwise computes client-side from the
  // ordered page (oldest → newest, balance += credit − debit).
  const balanceById = useMemo(() => {
    const map = new Map<number, number>();
    if (payments.length === 0) return map;

    const hasServerBalance = payments.some(
      (p) => p.running_balance !== null && p.running_balance !== undefined
    );
    if (hasServerBalance) {
      payments.forEach((p) => {
        map.set(p.id, Number(p.running_balance ?? p.previous_due ?? 0));
      });
      return map;
    }

    const sortedAsc = [...payments].sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime() ||
        a.id - b.id
    );
    // Seed with the previous_due on the earliest row (if set), otherwise 0.
    let running = Number(sortedAsc[0]?.previous_due || 0);
    sortedAsc.forEach((p) => {
      const { credit, debit } = classifyRow(p);
      running = running + credit - debit;
      map.set(p.id, running);
    });
    return map;
  }, [payments]);

  const handleEditPayment = async (payment: PaymentsProps) => {
    if (payment.status === 0) {
      await dispatch(setEditPayment(payment));
      dispatch(toggleCreateModal(1));
    } else if (payment.status === 1) {
      await dispatch(setEditPayment(payment));
      dispatch(toggleCreateModal(2));
    } else if (payment.status === 2) {
      await dispatch(setEditPayment(payment));
      dispatch(toggleCreateModal(3));
    }
  };

  const handleDeleteRow = async (id: number) => {
    const response = await confirmDelete(
      t.payments.deleteConfirmText,
      t.payments.deleteConfirmTitle,
      t.payments.deleteConfirmButton,
      true
    );

    if (response.confirmed) {
      setIsActionLoading(id);
      await handleDeletePayment(id, response.password);
      setIsActionLoading(0);
    }
  };

  // Route the report download to the source module's own PDF endpoint when the
  // payment originated from one (so the user gets the same PDF the source
  // module's listing page produces). Falls back to the generic payments report
  // for manually created invoices.
  const resolveReportEndpoint = (payment: PaymentsProps): string => {
    if (payment.process_receive_id) {
      return `/v1/process-receives/${payment.process_receive_id}/pdf`;
    }
    return `/v1/payments/${payment.id}/reports`;
  };

  const handlePrintReport = async (payment: PaymentsProps) => {
    const endpoint = resolveReportEndpoint(payment);
    try {
      await GET(endpoint, {})
        .then(async (response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, { ...(toastStyles as any) });
            const downloadLink = response.data.data.download_link;
            try {
              const newWindow = window.open(downloadLink, "_blank");
              if (!newWindow || newWindow.closed || typeof newWindow.closed == "undefined") {
                throw new Error("Pop-up blocked");
              }
            } catch (e) {
              window.location.href = downloadLink;
            }
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
          }
        })
        .catch((error: any) => {
          toast.error(error?.response?.data?.message || t.common.somethingWentWrong, { ...(toastStyles as any) });
        });
    } catch (error) {
      toast.error(t.common.somethingWentWrong, { ...(toastStyles as any) });
    }
  };

  const classify = classifyRow;

  // Is this payment row linked to a generic-engine process receive?
  const getProcessReceiveId = (p: PaymentsProps): number | null => {
    if (p.process_receive_id) return Number(p.process_receive_id);
    return null;
  };

  const handleOpenSource = (payment: PaymentsProps) => {
    const processReceiveId = getProcessReceiveId(payment);
    if (processReceiveId) {
      navigate(route.processReceivesView.replace(":id", String(processReceiveId)));
      return;
    }
  };

  // Details cell
  const renderDetails = (payment: PaymentsProps) => {
    const { isAdjustment } = classify(payment);
    const processReceiveId = getProcessReceiveId(payment);

    // Process Receive (generic engine)
    if (processReceiveId) {
      return (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-[#113F67] font-semibold">
            <ShoppingCart className="h-4 w-4" />
            <span>{t.payments.sourceLabels.processReceive}</span>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenSource(payment);
            }}
            className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-[#113F67] hover:underline"
            title={t.payments.tooltips.openProcessReceive}
          >
            <span>
              ID: {processReceiveId} ·{" "}
              {moment(payment.created_at).format("DD MMM, YYYY")}
            </span>
            <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      );
    }

    if (isAdjustment) {
      const subtitle =
        payment.type === 0
          ? t.payments.details.amountAdded
          : t.payments.details.amountDeducted;
      return (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-[#113F67] font-semibold">
            <ArrowLeftRight className="h-4 w-4" />
            <span>{payment.note || t.payments.types.adjustment}</span>
          </div>
          <div className="text-xs text-gray-500">{subtitle}</div>
        </div>
      );
    }

    // Received payment
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-1.5 text-[#113F67] font-semibold">
          <Wallet className="h-4 w-4" />
          <span>{t.payments.actions.receivedPayment}</span>
        </div>
        {payment.note && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <StickyNote className="h-3 w-3" />
            <span>{payment.note}</span>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card className="p-8 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#113F67]" />
        <p className="text-gray-500 mt-2">{t.payments.loading}</p>
      </Card>
    );
  }

  if (payments.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-gray-500">{t.payments.noRecords}</p>
      </Card>
    );
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block">
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold text-brand-dark w-12">{t.payments.table.srNo}</TableHead>
                <TableHead className="font-semibold text-brand-dark">{t.payments.table.paymentId}</TableHead>
                <TableHead className="font-semibold text-brand-dark">{t.payments.table.details}</TableHead>
                <TableHead className="font-semibold text-brand-dark text-right">{(t.payments.table as any).credit}</TableHead>
                <TableHead className="font-semibold text-brand-dark text-right">{(t.payments.table as any).debit}</TableHead>
                <TableHead className="font-semibold text-brand-dark text-right">{(t.payments.table as any).balance}</TableHead>
                <TableHead className="font-semibold text-brand-dark">{t.payments.table.date}</TableHead>
                <TableHead className="font-semibold text-brand-dark text-right">{t.payments.table.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((payment, index) => {
                const { credit, debit } = classify(payment);
                const rowClickable = getProcessReceiveId(payment) !== null;
                return (
                  <TableRow
                    key={payment.id}
                    className={`align-top ${rowClickable ? "cursor-pointer hover:bg-rose-50/40" : ""}`}
                    onClick={rowClickable ? () => handleOpenSource(payment) : undefined}
                  >
                    <TableCell className="text-gray-500">{(pageNumber - 1) * pageSize + index + 1}</TableCell>
                    <TableCell className="font-semibold text-gray-800">{payment.id}</TableCell>
                    <TableCell>{renderDetails(payment)}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-700">
                      {credit > 0 ? formatINR(credit) : "0.00"}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-rose-700">
                      {debit > 0 ? formatINR(debit) : "0.00"}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-[#113F67]">
                      {formatINR(balanceById.get(payment.id) ?? payment.previous_due)}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="text-gray-700">{moment(payment.created_at).format("DD-MM-YYYY")}</div>
                      <div className="text-xs text-gray-400">{moment(payment.created_at).format("DD-MM-YYYY hh:mm A")}</div>
                    </TableCell>
                    <TableCell
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isActionLoading === payment.id ? (
                        <Loader2 className="w-4 h-4 animate-spin ml-auto" />
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          {payment.status === 0 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-lg hover:bg-orange-50"
                              onClick={() => handlePrintReport(payment)}
                              title={
                                isSourceLinked(payment)
                                  ? (t.payments as any).sourceManaged?.pdfTooltip
                                  : (t.payments as any).pdfButton || "PDF"
                              }
                            >
                              <FileText className="w-4 h-4 text-orange-500" />
                            </Button>
                          )}
                          {payment.status !== 0 && !isSourceLinked(payment) && canAccess("parties.payment") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10"
                              onClick={() => handleEditPayment(payment)}
                            >
                              <Pencil className="w-4 h-4 text-[#113F67]" />
                            </Button>
                          )}
                          {!isSourceLinked(payment) && canAccess("parties.payment") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-lg hover:bg-red-50"
                              onClick={() => handleDeleteRow(payment.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {payments.map((payment, index) => {
          const { credit, debit } = classify(payment);
          const rowClickable = getProcessReceiveId(payment) !== null;
          return (
            <Card
              key={payment.id}
              className={`p-4 ${rowClickable ? "cursor-pointer" : ""}`}
              onClick={rowClickable ? () => handleOpenSource(payment) : undefined}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">#{(pageNumber - 1) * pageSize + index + 1}</span>
                <span className="text-sm text-gray-400">ID: {payment.id}</span>
              </div>
              <div className="mb-3">{renderDetails(payment)}</div>
              <div className="grid grid-cols-3 gap-2 text-sm border-t pt-3">
                <div>
                  <div className="text-xs text-gray-500">{(t.payments.table as any).credit}</div>
                  <div className="font-semibold text-emerald-700">₹{credit > 0 ? formatINR(credit) : "0.00"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">{(t.payments.table as any).debit}</div>
                  <div className="font-semibold text-rose-700">₹{debit > 0 ? formatINR(debit) : "0.00"}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">{(t.payments.table as any).balance}</div>
                  <div className="font-semibold text-[#113F67]">₹{formatINR(balanceById.get(payment.id) ?? payment.previous_due)}</div>
                </div>
              </div>
              <div
                className="flex items-center justify-between mt-3 pt-3 border-t"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="text-xs text-gray-400">
                  {moment(payment.created_at).format("DD-MM-YYYY hh:mm A")}
                </span>
                <div className="flex items-center gap-1">
                  {isActionLoading === payment.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      {payment.status === 0 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg hover:bg-orange-50"
                          onClick={() => handlePrintReport(payment)}
                          title={
                            isSourceLinked(payment)
                              ? (t.payments as any).sourceManaged?.pdfTooltip
                              : (t.payments as any).pdfButton || "PDF"
                          }
                        >
                          <FileText className="w-4 h-4 text-orange-500" />
                        </Button>
                      )}
                      {payment.status !== 0 && !isSourceLinked(payment) && canAccess("parties.payment") && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10"
                          onClick={() => handleEditPayment(payment)}
                        >
                          <Pencil className="w-4 h-4 text-[#113F67]" />
                        </Button>
                      )}
                      {!isSourceLinked(payment) && canAccess("parties.payment") && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg hover:bg-red-50"
                          onClick={() => handleDeleteRow(payment.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );
};

export default PaymentList;
